import { createSelector } from "reselect"

import { EmployeeHolidayRecord, EmployeePTORecord } from "../cached-data/types"
import { ReduxState, TkSummary } from "../common/types"

export const selectPTOObjects = (state: ReduxState): Record<number, EmployeePTORecord> => state.entities.employeePTO?.objects ?? {}

export const selectHolidayObjects = (state: ReduxState): Record<string, EmployeeHolidayRecord> => state.entities.employeeHolidays?.objects ?? {}

const ptoObjectsSelector = selectPTOObjects
const holidayObjectsSelector = selectHolidayObjects
const tkSummarySelector = (state: ReduxState) => state.exceptions.tkSummary

/**
 * Compute fulfillment for every PTO and holiday record, using the provided tkSummary as the
 * source of truth. Extracted from {@link selectPTOFulfillment} so callers that hold the raw
 * entity data (e.g. class components via `mapStateToProps`) can compute fulfillment without
 * reconstructing a Redux state object.
 *
 * Returns an empty object when `tkSummary` is null or undefined.
 *
 * Fulfillment rules:
 *   - **ABSENCE_TYPE**: a tkSummary row for the employee on the record's date has the
 *     record's `absence_type_id` in its `absence_type_ids` array.
 *   - **COST_CODE**: the sum of `st` minutes across rows for the employee on the date
 *     whose `cost_code.pto_bucket_id` matches `pto_bucket_id` is ≥ `hours × 60`.
 *     Records with null or zero `hours` are never fulfilled.
 *
 * @param ptoObjects - EmployeePTO records keyed by ID.
 * @param holidayObjects - EmployeeHoliday records keyed by ID.
 * @param tkSummary - TkSummary data, or null/undefined if not yet fetched.
 * @returns A map of record ID (string) → fulfilled boolean.
 */
export const computePTOFulfillment = (
    ptoObjects: Record<number, EmployeePTORecord>,
    holidayObjects: Record<string, EmployeeHolidayRecord>,
    tkSummary: TkSummary | null | undefined
): Record<string, boolean> => {
    if (!tkSummary) return {}

    const result: Record<string, boolean> = {}
    const allRecords: Array<EmployeePTORecord | EmployeeHolidayRecord> = [...Object.values(ptoObjects), ...Object.values(holidayObjects)]

    for (const record of allRecords) {
        const dateRows = (tkSummary[String(record.employee)] ?? []).filter(row => row.shift_date === record.date)

        let fulfilled = false
        if (record.pto_bucket_type === "ABSENCE_TYPE") {
            const absenceTypeId = record.absence_type_id
            if (absenceTypeId !== null) {
                fulfilled = dateRows.some(row => row.absence_type_ids?.includes(absenceTypeId))
            }
        } else if (record.pto_bucket_type === "COST_CODE") {
            if (record.hours !== null && record.hours > 0) {
                const totalSt = dateRows.filter(row => row.cost_code?.pto_bucket_id === record.pto_bucket_id).reduce((sum, row) => sum + (row.st ?? 0), 0)
                fulfilled = totalSt / 60 >= record.hours
            }
        }

        result[String(record.id)] = fulfilled
    }

    return result
}

/**
 * Cost-code reference shape needed by {@link computeCellPTOStatus} to resolve
 * the `pto_bucket_id` of a cost-code-scoped row. Looser than the full iCostCode
 * type so this module stays free of cell-renderer imports.
 */
type PTOCostCodeRef = { pto_bucket_id?: number | null }

type PTORecord = EmployeePTORecord | EmployeeHolidayRecord

const ptoRecordKey = (employeeId: number | string, date: string): string => `${employeeId}|${date}`

/**
 * Group PTO + holiday records by `(employee, date)` so cell-level lookups don't
 * have to filter `Object.values(...)` on every call.
 *
 * Used as the input to {@link computeCellPTOStatus}. Build once per
 * fulfillment-input change (the {@link selectPTORecordsByEmployeeDate} selector
 * memoizes by reference equality on the entity dicts).
 */
export const buildPTORecordsByEmployeeDate = (ptoObjects: Record<number, EmployeePTORecord>, holidayObjects: Record<string, EmployeeHolidayRecord>): Map<string, PTORecord[]> => {
    const map = new Map<string, PTORecord[]>()
    const add = (record: PTORecord): void => {
        const key = ptoRecordKey(record.employee, record.date)
        const existing = map.get(key)
        if (existing) existing.push(record)
        else map.set(key, [record])
    }
    for (const record of Object.values(ptoObjects)) add(record)
    for (const record of Object.values(holidayObjects)) add(record)
    return map
}

/**
 * Memoized selector returning the `(employee, date) → records` index used by
 * {@link computeCellPTOStatus}.
 */
export const selectPTORecordsByEmployeeDate = createSelector(ptoObjectsSelector, holidayObjectsSelector, buildPTORecordsByEmployeeDate)

/**
 * Compute whether a given (employee, date, optional cost code) cell has any PTO
 * or holiday request and whether every matching request is already fulfilled.
 * Also returns the deduplicated display names of all matching requests (holiday
 * name for holiday records, "Employee Request" for employee PTO records) so
 * callers can render a tooltip.
 *
 * Shared by the weekly timecard cell renderer (palm-tree icon) and the fancy
 * search "PTO" find-category so both stay in sync.
 *
 * For cost-code-scoped cells, only requests whose `pto_bucket_id` matches the
 * cost code's `pto_bucket_id` are considered — otherwise unrelated PTO buckets
 * would light up every cost-code row for the employee on that day.
 *
 * @param params.employeeId - Employee ID; if null/undefined, returns the empty result.
 * @param params.date - Date string (YYYY-MM-DD); if null/undefined, returns the empty result.
 * @param params.costCodeId - Optional cost code ID; when provided the result is scoped to
 *   requests matching that cost code's bucket.
 * @param params.recordsByEmployeeDate - Pre-grouped PTO/holiday records, as built by
 *   {@link buildPTORecordsByEmployeeDate} or {@link selectPTORecordsByEmployeeDate}.
 * @param params.ptoFulfillment - Map from {@link computePTOFulfillment}.
 * @param params.costCodes - Cost code objects keyed by ID (only consulted when costCodeId is set).
 * @returns `{ ptoRequested, ptoFulfilled, ptoNames }`. `ptoFulfilled` is only true when at
 *   least one request matched AND every matching request is fulfilled.
 */
export const computeCellPTOStatus = (params: {
    employeeId: number | null | undefined
    date: string | null | undefined
    costCodeId?: number | null
    recordsByEmployeeDate: Map<string, PTORecord[]>
    ptoFulfillment: Record<string, boolean>
    costCodes?: Record<number, PTOCostCodeRef>
}): { ptoRequested: boolean; ptoFulfilled: boolean; ptoNames: string[] } => {
    const { employeeId, date, costCodeId, recordsByEmployeeDate, ptoFulfillment, costCodes } = params
    if (!employeeId || !date) return { ptoRequested: false, ptoFulfilled: false, ptoNames: [] }

    let allRecords = recordsByEmployeeDate.get(ptoRecordKey(employeeId, date)) ?? []

    if (costCodeId != null) {
        const ptoBucketId = costCodes?.[costCodeId]?.pto_bucket_id ?? null
        allRecords = ptoBucketId != null ? allRecords.filter(r => r.pto_bucket_id === ptoBucketId) : []
    }

    const ptoRequested = allRecords.length > 0
    const nameSet = new Set<string>()
    for (const record of allRecords) {
        nameSet.add(typeof record.id === "string" ? (record as EmployeeHolidayRecord).name : "Employee Request")
    }

    const ptoFulfilled = ptoRequested && allRecords.every(r => ptoFulfillment[String(r.id)] === true)
    return { ptoRequested, ptoFulfilled, ptoNames: Array.from(nameSet).sort() }
}

/**
 * Memoized selector that computes PTO/holiday fulfillment from the Redux store.
 *
 * @returns A map of record ID (string) → fulfilled boolean covering both PTO and
 *   holiday records. IDs do not overlap between the two entity types.
 */
export const selectPTOFulfillment = createSelector(ptoObjectsSelector, holidayObjectsSelector, tkSummarySelector, computePTOFulfillment)

/**
 * Groups all PTO and holiday records in Redux by their date.
 *
 * @returns A map from YYYY-MM-DD date string to all PTO/holiday records on that date.
 */
export const selectPTORecordsByDate = createSelector(
    ptoObjectsSelector,
    holidayObjectsSelector,
    (ptoObjects, holidayObjects): Map<string, PTORecord[]> => {
        const map = new Map<string, PTORecord[]>()
        const add = (record: PTORecord): void => {
            const existing = map.get(record.date)
            if (existing) existing.push(record)
            else map.set(record.date, [record])
        }
        for (const record of Object.values(ptoObjects)) add(record)
        for (const record of Object.values(holidayObjects)) add(record)
        return map
    }
)

/**
 * Like {@link selectPTORecordsByDate} but filtered to only records not yet fulfilled.
 * Dates with no unfulfilled records are omitted from the result.
 *
 * @returns A map from YYYY-MM-DD date string to unfulfilled PTO/holiday records on that date.
 */
export const selectUnfulfilledPTORecordsByDate = createSelector(
    selectPTORecordsByDate,
    selectPTOFulfillment,
    (byDate, fulfillment): Map<string, PTORecord[]> => {
        const result = new Map<string, PTORecord[]>()
        for (const [date, records] of byDate) {
            const unfulfilled = records.filter(r => !fulfillment[String(r.id)])
            if (unfulfilled.length > 0) result.set(date, unfulfilled)
        }
        return result
    }
)
